import {Injectable, Type, inject} from '@angular/core';
import {firstValueFrom} from 'rxjs';
import {map, take} from 'rxjs/operators';
import {ModalConfig, ModalRef, ModalService as UiModalService} from '@sms-ir/ui-components';

import {ModifyTextMessageModalComponent} from '@app/main/modules/report/components/bank-number-report/modify-text-message-modal/modify-text-message-modal.component';
import {ShowDataModalInterface} from '../components/show-data-modal/models/show-data-modal.interface';
import {ShowDataModalComponent} from '../components/show-data-modal/show-data-modal.component';
import {ConfirmModalComponent} from '../components/confirm-modal/confirm-modal.component';
import {OneInputModalComponent} from '../components/one-input-modal/one-input-modal.component';
import {DeleteModalComponent} from '../components/delete-modal/delete-modal.component';
import {
  BootstrapperNotifyModalComponent,
} from '@shared/components/bootstrapper-notify-modal/bootstrapper-notify-modal.component';
import {VerificationModalComponent} from '@shared/components/verification-modal/verification-modal.component';
import {PlanAccessModalComponent} from '@shared/components/plan-access-modal/plan-access-modal.component';
import {IpeLoadingModalComponent} from '@shared/components/ipe-loading-modal/ipe-loading-modal.component';

type ModalSize = NonNullable<ModalConfig['size']>;

@Injectable({
  providedIn: 'root',
})
export class ModalService {
  private readonly uiModal = inject(UiModalService);
  private planAccessPromise: Promise<boolean> | null = null;

  private openModal<T, TData = unknown, TRef = unknown>(
    component: Type<T>,
    ariaLabel: string,
    options: {
      data?: TData;
      size?: ModalSize;
      maskClosable?: boolean;
      keyboard?: boolean;
    } = {},
  ): ModalRef<TRef> {
    return this.uiModal.open(component, {
      size: options.size ?? 'md',
      mask: true,
      maskClosable: options.maskClosable ?? true,
      keyboard: options.keyboard ?? true,
      ariaLabel,
      data: options.data,
    });
  }

  /**
   * Resolves with the close value. Dismiss / null / false reject with 'dismissed'.
   * Empty string and other falsy-but-valid values (0) are accepted.
   */
  private awaitResult<T>(modalRef: ModalRef<T | false | null | undefined>): Promise<T> {
    return firstValueFrom(
      modalRef.afterClosed$.pipe(
        take(1),
        map((result) => {
          if (result == null || result === false) {
            throw new Error('dismissed');
          }
          return result as T;
        }),
      ),
    );
  }

  async oneInput(
    initialValue: string,
    text: string,
    header: string,
    modalConfirmButton?: string,
    modalCancelButton?: string,
    svg?: string,
    inputConf?: {
      placeholder?: string;
      maxLength?: number;
      minLength?: null;
      type?: string;
    },
  ): Promise<string> {
    return this.awaitResult(
      this.openModal(OneInputModalComponent, header || 'ورودی', {
        data: {
          initialValue,
          modalText: text,
          modalHeader: header,
          modalConfirmButton,
          modalCancelButton,
          inputConf,
          svg,
        },
      }),
    );
  }

  async delete(
    text: string,
    header: string,
    modalConfirmButton?: string,
    modalCancelButton?: string,
    _svg?: string,
  ): Promise<boolean> {
    return this.awaitResult(
      this.openModal(DeleteModalComponent, header || 'حذف', {
        data: {
          modalText: text,
          modalHeader: header,
          modalConfirmButton,
          modalCancelButton,
        },
      }),
    );
  }

  async confirm(
    text: string,
    header: string,
    modalConfirmButton?: string,
    modalCancelButton?: string,
    _svg?: string,
  ): Promise<boolean> {
    return this.awaitResult(
      this.openModal(ConfirmModalComponent, header || 'تایید', {
        data: {
          modalText: text,
          modalHeader: header,
          modalConfirmButton,
          modalCancelButton,
        },
      }),
    );
  }

  async showData(text: string, header: string, data: ShowDataModalInterface, svg?: string): Promise<void> {
    this.openModal(ShowDataModalComponent, header || 'نمایش اطلاعات', {
      data: {
        modalText: text,
        modalHeader: header,
        data,
        svg,
      },
    });
  }

  async BootstrapperNotify(
    messages: {title: string; id: number; showAtEntry: boolean; content: string}[],
  ): Promise<number[]> {
    const modalRef = this.openModal<
      BootstrapperNotifyModalComponent,
      {modalHeader: string; messages: typeof messages},
      number[]
    >(BootstrapperNotifyModalComponent, 'اعلانات مهم', {
      maskClosable: false,
      data: {
        modalHeader: 'اعلانات مهم',
        messages,
      },
    });

    return firstValueFrom(
      modalRef.afterClosed$.pipe(
        take(1),
        map((ids) => (Array.isArray(ids) ? ids : [])),
      ),
    );
  }

  async modifyBankNumberTextMessage(text: string): Promise<string> {
    return this.awaitResult(
      this.openModal(ModifyTextMessageModalComponent, 'ویرایش متن پیامک', {
        data: {defaultText: text},
      }),
    );
  }

  async templateConfirmation(title: string = 'توجه'): Promise<boolean> {
    const description =
      'متن شما حاوی کاراکتر ویژه # می‌باشد. کاربرد این کاراکتر زمانی است که به مخاطبان دفترچه‌تلفن خود ارسال نمایید. \n در غیراینصورت، به همان ترتیب در متن نهایی، درج خواهد شد.';
    return this.confirm(description, title, 'تایید', 'اصلاح مجدد');
  }

  async verification(isPending: boolean): Promise<boolean> {
    return this.awaitResult(
      this.openModal<VerificationModalComponent, {isPending: boolean}, boolean>(
        VerificationModalComponent,
        'احراز هویت',
        {
          data: {isPending},
        },
      ),
    );
  }

  async planAccess(): Promise<boolean> {
    if (this.planAccessPromise) {
      return this.planAccessPromise;
    }

    const promise = this.awaitResult(
      this.openModal<PlanAccessModalComponent, undefined, boolean>(
        PlanAccessModalComponent,
        'ارتقا پلن',
        {size: '4xl'},
      ),
    ).finally(() => {
      this.planAccessPromise = null;
    });

    this.planAccessPromise = promise;
    return promise;
  }

  loading(): ModalRef {
    return this.openModal(IpeLoadingModalComponent, 'در حال بارگذاری', {
      size: 'sm',
      maskClosable: false,
      keyboard: false,
    });
  }
}
